import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import React, { FC, useMemo } from 'react';
import { useLocation } from 'react-router';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import Icon from '~ComponentsImproved/Icon';
import ArrowForwardIcon from '~Assets/icons/arrow_forward.svg';
import PhoneIcon from '~Assets/icons/phone_android.svg';
import { useButton, useCurrency } from '~Hooks';
import { SearchOrders_searchOrders_results } from './__queries__';
import { Box } from '~Styles';

const OrderRowContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 8px 16px;
  text-transform: none;
  margin-bottom: 8px;
  height: 48px;
  box-sizing: border-box;
  align-items: center;
  position: relative;
  overflow: hidden;

  &:active {
    background-color: var(--colorBlueGray900);
  }

  border: ${({ isSelected }) =>
    isSelected ? '1px solid var(--colorBlueGray700)' : '1px solid var(--colorBlueGray1100)'};
`;

const OrderRowDetails = styled.div`
  color: var(--colorWhite);
  display: flex;
  font-weight: 600;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: calc(100% - 30px);

  & > div:nth-of-type(2) {
    text-align: center;
  }
`;

const OrderColor = styled.div<{ color: string }>`
  position: absolute;
  left: 0;
  height: 100%;
  top: 0;
  width: 4px;
  background-color: ${({ color }) => color};
`;

const ExpressTag = styled.span`
  border: 1px solid var(--colorWhite);
  display: inline-block;
  margin-right: 2px;
  padding: 2px 4px;
  border-radius: 2px;
  font-size: 10px;
  margin-top: 1px;
`;

/**
 * Get the color indicator for an order based on its status
 */
const getOrderColor = (status: ORDER_STATUS) => {
  switch (status) {
    case ORDER_STATUS.COMPLETED:
      return 'var(--colorUIGreen)';
    case ORDER_STATUS.REFUNDED:
      return 'var(--colorUIRed)';
    case ORDER_STATUS.ACCEPTED:
      return 'var(--colorUIYellow)';
    default:
      return 'var(--colorBlueGray1300)';
  }
};

interface OrderRowProps {
  order: SearchOrders_searchOrders_results;
  onOrderClick: () => void;
}

/**
 * Individual order row component that manages its own selection state
 * by reading the orderId from the URL
 */
const OrderRow: FC<OrderRowProps> = ({ order, onOrderClick }) => {
  const currency = useCurrency();
  const location = useLocation();

  // Extract the selected order ID from URL params
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedOrderId = searchParams.get('orderId');

  // Check if this specific order is selected
  const isSelected = selectedOrderId === order.id;

  // Handle order click - update URL with orderId parameter
  const orderButton = useButton({
    onClick: onOrderClick,
  });

  return (
    <OrderRowContainer {...orderButton} isSelected={isSelected}>
      <OrderColor color={getOrderColor(order.status)} />
      <OrderRowDetails>
        <Box display="flex" alignItems="center" gap="xs">
          {order.origin === 'APP' && (
            <Icon src={PhoneIcon} color="var(--colorWhite)" height="24px" />
          )}
          {order.origin === 'EXPRESS' && <ExpressTag>EXPRESS</ExpressTag>}
          {'#' + order.easyId}
        </Box>
        <span>{DateTime.fromISO(order.createdAt!).toFormat('HH:mm')}</span>
        <span>{currency.format(order.paymentAmount)}</span>
      </OrderRowDetails>
      {isSelected && <Icon src={ArrowForwardIcon} color="var(--colorWhite)" height="24px" />}
    </OrderRowContainer>
  );
};

export default OrderRow;
